import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "../../tenant/tenant-list/tenant-filter-options/filter-options-tenant.scss";

interface DateFilterProps {
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  onDateRangeChange: (start: string | null, end: string | null) => void;
  startDateError?: string;
  endDateError?: string;
}

const DateFilter: React.FC<DateFilterProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
  startDateError,
  endDateError,
}) => {
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    startDate ? dayjs(startDate) : null,
  );

  const [toDate, setToDate] = useState<Dayjs | null>(
    endDate ? dayjs(endDate) : null,
  );

  const [errorStart, setErrorStart] = useState<string | null>(
    startDateError ?? null,
  );

  const [errorEnd, setErrorEnd] = useState<string | null>(endDateError ?? null);

  useEffect(() => {
    setFromDate(startDate ? dayjs(startDate) : null);
  }, [startDate]);

  useEffect(() => {
    setToDate(endDate ? dayjs(endDate) : null);
  }, [endDate]);

  useEffect(() => {
    setErrorStart(startDateError ?? null);
  }, [startDateError]);

  useEffect(() => {
    setErrorEnd(endDateError ?? null);
  }, [endDateError]);

  const handleStartDateChange = (newValue: Dayjs | null) => {
    const isInvalid =
      !newValue ||
      !newValue.isValid() ||
      newValue.year() < 1900 ||
      newValue.isAfter(dayjs());

    if (isInvalid) {
      setFromDate(null);
      setErrorStart("Invalid date");

      onDateRangeChange(null, toDate ? toDate.format("YYYY-MM-DD") : null);

      return;
    }

    if (toDate && newValue.isAfter(toDate)) {
      setErrorStart("From Date cannot be after To Date");
      return;
    }

    setErrorStart(null);
    setFromDate(newValue);

    onDateRangeChange(
      newValue.format("YYYY-MM-DD"),
      toDate ? toDate.format("YYYY-MM-DD") : null,
    );
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    const isInvalid =
      !newValue ||
      !newValue.isValid() ||
      newValue.year() < 1900 ||
      newValue.isAfter(dayjs());

    if (isInvalid) {
      setToDate(null);
      setErrorEnd("Invalid date");

      onDateRangeChange(fromDate ? fromDate.format("YYYY-MM-DD") : null, null);

      return;
    }

    if (fromDate && newValue.isBefore(fromDate)) {
      setErrorEnd("To Date cannot be before From Date");
      return;
    }

    setErrorEnd(null);
    setToDate(newValue);

    onDateRangeChange(
      fromDate ? fromDate.format("YYYY-MM-DD") : null,
      newValue.format("YYYY-MM-DD"),
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="tenant-filter-date-input-wrapper">
        <DesktopDatePicker
          label="From Date"
          value={fromDate}
          onChange={handleStartDateChange}
          disableFuture
          minDate={dayjs("1900-01-01")}
          maxDate={toDate ?? dayjs()}
          shouldDisableDate={(date) =>
            !!(toDate && date.isAfter(toDate)) || date.isAfter(dayjs())
          }
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              error: !!errorStart,
              helperText: errorStart,
              sx: {
                "& label.MuiInputLabel-shrink": {
                  fontSize: "12px",
                  padding: 0,
                  margin: 0,
                },
              },
            },
          }}
        />

        <DesktopDatePicker
          label="To Date"
          value={toDate}
          onChange={handleEndDateChange}
          disableFuture
          minDate={fromDate ?? dayjs("1900-01-01")}
          maxDate={dayjs()}
          shouldDisableDate={(date) =>
            !!(fromDate && date.isBefore(fromDate)) || date.isAfter(dayjs())
          }
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              error: !!errorEnd,
              helperText: errorEnd,
              sx: {
                "& label.MuiInputLabel-shrink": {
                  fontSize: "12px",
                  padding: 0,
                  margin: 0,
                },
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DateFilter;
